import React, { useEffect, useMemo, useRef, useState } from "react";
import { Camera, KeyRound, Loader2, ShieldAlert } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";

export const QrScannerCard = ({ title, description, onVerify, verifying }) => {
  const scannerRef = useRef(null);
  const scannerId = useMemo(() => `handover-scanner-${Math.random().toString(36).slice(2)}`, []);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [manualToken, setManualToken] = useState("");
  const [cameraReady, setCameraReady] = useState(false);

  useEffect(() => {
    let scanner;
    let active = true;

    const startScanner = async () => {
      if (!cameraOpen) return;
      setCameraError("");
      setCameraReady(false);

      try {
        scanner = new Html5Qrcode(scannerId);
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 220, height: 220 } },
          async (decodedText) => {
            if (!active) return;
            await onVerify(decodedText, "qr");
            setManualToken(decodedText);
            await scanner.stop();
            await scanner.clear();
            scannerRef.current = null;
            setCameraOpen(false);
          },
          () => {}
        );
        if (active) setCameraReady(true);
      } catch (error) {
        if (!active) return;
        setCameraError("Camera access was unavailable. You can still paste the verification code manually.");
        setCameraOpen(false);
        scannerRef.current = null;
      }
    };

    startScanner();

    return () => {
      active = false;
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {}).finally(() => {
          scannerRef.current?.clear().catch(() => {});
          scannerRef.current = null;
        });
      }
    };
  }, [cameraOpen, onVerify, scannerId]);

  const handleManualSubmit = async (event) => {
    event.preventDefault();
    if (!manualToken.trim()) return;
    await onVerify(manualToken.trim(), "manual");
  };

  return (
    <div className="rounded-3xl border border-[#E2E8F0] bg-white p-5 shadow-sm">
      <h3 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">{title}</h3>
      <p className="mt-2 text-sm leading-6 text-[#64748B]">{description}</p>

      <div className="mt-5 rounded-3xl border border-dashed border-[#CBD5E1] bg-[#F8FAFC] p-4">
        {cameraOpen ? (
          <div>
            <div id={scannerId} className="overflow-hidden rounded-2xl bg-black" />
            {!cameraReady && (
              <div className="mt-3 inline-flex items-center gap-2 text-sm text-[#475569]">
                <Loader2 size={14} className="animate-spin" />
                Starting camera…
              </div>
            )}
          </div>
        ) : (
          <div className="text-sm text-[#64748B]">
            <div className="flex items-start gap-3 rounded-2xl bg-white p-4 shadow-sm">
              <Camera size={18} className="mt-0.5 text-[#163D6B]" />
              <div>
                <p className="font-medium text-[#0F172A]">Use the camera when available</p>
                <p className="mt-1 leading-6">If permissions are denied or the device does not support scanning, the manual verification field below remains available.</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {cameraError && (
        <div className="mt-4 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <ShieldAlert size={16} className="mt-0.5" />
          <p>{cameraError}</p>
        </div>
      )}

      <div className="mt-5 flex flex-wrap gap-3">
        <button
          onClick={() => setCameraOpen((current) => !current)}
          className="inline-flex items-center gap-2 rounded-xl bg-[#163D6B] px-4 py-3 text-sm font-semibold text-white"
        >
          <Camera size={16} />
          {cameraOpen ? "Close scanner" : "Open camera scanner"}
        </button>
      </div>

      <form className="mt-5 space-y-3" onSubmit={handleManualSubmit}>
        <label className="text-sm font-medium text-[#0F172A]">Manual verification code</label>
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <input
              value={manualToken}
              onChange={(event) => setManualToken(event.target.value)}
              placeholder="Paste or type the QR verification code"
              className="w-full rounded-2xl border border-[#CBD5E1] bg-white px-10 py-3 text-sm outline-none transition focus:border-[#163D6B] focus:ring-4 focus:ring-[#163D6B]/10"
            />
          </div>
          <button
            type="submit"
            disabled={verifying || !manualToken.trim()}
            className="inline-flex items-center justify-center rounded-2xl border border-[#E2E8F0] bg-white px-4 py-3 text-sm font-semibold text-[#0F172A] disabled:opacity-60"
          >
            {verifying ? "Verifying…" : "Confirm manually"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default QrScannerCard;
